import recaptcha from '@components/App/RecaptchaWidget/recaptcha'
import { LoaderActions } from '@store/loaders'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import axios from 'axios'
import { useDispatch, useSelector, useStore } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { normalize } from '@/helpers'
import { useProjectAccess } from '@/hooks'
import { EXPORT_PERMISSION } from '@/permissions/project/definitions'
import { selectIsLoading } from '@/store'
import { normalizeData } from './normalizeData'

const EXPORT_URL: string | undefined = import.meta.env.VITE_EXPORT_PDF_API_URL

export const useExportAsDoc = () => {
  const dispatch = useDispatch()

  const projectTitle = useSelector(migrationProjectSelector.getProjectTitle)
  const isFetching = useSelector(selectIsLoading('export_doc'))

  const { hasAccess } = useProjectAccess(EXPORT_PERMISSION.FILE_FORMATS)

  const store = useStore<AppState>()

  return async () => {
    if (isFetching || !hasAccess || !EXPORT_URL) {
      return
    }

    sendGtmEvent(GtmTriggerName.EXPORT_DOCX)

    const recaptchaResponseKey = await recaptcha.execute({
      action: 'useExportAsDoc',
    })

    const content = normalizeData(
      migrationProjectSelector.getNodesWithBlocks(store.getState())
    )

    try {
      dispatch(LoaderActions.set('export_doc'))

      const response = await axios.post(
        `${EXPORT_URL}/export-doc?responseKey=${recaptchaResponseKey}`,
        { content },
        { responseType: 'blob' }
      )

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      })

      const downloadUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `${normalize(projectTitle)}.docx`
      link.click()

      URL.revokeObjectURL(downloadUrl)
    } finally {
      dispatch(LoaderActions.unset('export_doc'))
    }
  }
}
